<template lang="pug">
button(
  :type="type"
  @click="onClick"
  :class="classes.join(' ')"
  :disabled="disabled || loading || selfLoading")

  div(
    v-if="!btnWithPadding"
    :class="{[$style.iconAlign]: iconLeft}"
    :style="iconLeft ? iconStyles.join(';') : 'min-width: 25px'")
    Icon(
      v-if="iconLeft"
      :color="iconColor"
      :name="iconLeft"
      :size="iconFontSize")

  div(:class="['lg:px-ds-md whitespace-nowrap', $style.slot]")
    // @slot Button text
    slot

  div(
    v-if="!btnWithPadding"
    :class="{[$style.iconAlign]: iconRight}"
    :style="iconRight ? iconStyles.join(';') : 'min-width: 25px'")
    svg.block(
      v-if="loading || selfLoading"
      :width="iconFontSize*1.25"
      :height="iconFontSize*1.25"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid")
      circle(
        cx="50"
        cy="50"
        fill="none"
        stroke="white"
        stroke-width="15"
        r="35"
        stroke-dasharray="164.93361431346415 56.97787143782138")
        animateTransform(
          attributeName="transform"
          type="rotate"
          repeatCount="indefinite"
          dur="1s"
          values="0 50 50;360 50 50"
          keyTimes="0;1")
    Icon(
      v-else-if="iconRight"
      :name="iconRight"
      :size="iconFontSize")
</template>

<script>
import { getSizeClasses } from "~/scripts/css-helper"

/** Wrapper around the standard `<button></button>` html tag. */
export default {
  name: "Btn", // eslint-disable-line vue/multi-word-component-names

  props: {
    /** Standard button types */
    type: {
      type: String,
      default: "button"
    },
    /** Colour schema */
    kind: {
      type: String,
      default: "primary",
      validator: value =>
        [
          "primary",
          "red",
          "light",
          "superlight",
          "green",
          "gray",
          "gray-100",
          "gray-140",
          "lightgray",
          "yellow",
          "darkgray",
          "cta-secondary",
          "rebrand-cta",
          "white"
        ].includes(value)
    },
    size: {
      type: String,
      default: "m",
      validator: value => ["xs", "s", "xm", "m", "l", "xl"].includes(value)
    },
    /** Full width element */
    block: {
      type: Boolean,
      default: false
    },
    /** Display just the slot, without icons placeholders */
    btnWithPadding: {
      type: Boolean,
      default: false
    },
    /** Icon shortcut */
    iconLeft: {
      type: String,
      default: ""
    },
    /** Icon shortcut */
    iconRight: {
      type: String,
      default: ""
    },
    /** Icon size */
    // eslint-disable-next-line vue/require-default-prop
    iconSize: {
      type: Number,
      required: false
    },
    /** Icon colour */
    // eslint-disable-next-line vue/require-default-prop
    iconColor: {
      type: String,
      required: false
    },

    sharpCornersLeft: {
      type: Boolean,
      default: false
    },

    sharpCornersRight: {
      type: Boolean,
      default: false
    },
    /** Nuxt router push call (create a link) */
    to: {
      type: String,
      default: ""
    },
    /** Button does nothing */
    inactive: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    stopClick: {
      type: Boolean,
      default: false
    },
    target: {
      type: String,
      default: ""
    },
    wpBlock: {
      type: Object,
      default: () => {},
      required: false
    }
  },

  data() {
    return {
      selfLoading: false
    }
  },

  computed: {
    classes() {
      const classes = []
      classes.push(this.$style[this.kind])
      classes.push(...getSizeClasses(this.size))

      if (this.block) {
        classes.push(this.$style.block)
      }

      // This is intended to be used to get the styles from WP CORE block
      if (this.wpBlock) {
        const block = this.wpBlock
        let bgColour, textColour
        if (block.attributes) {
          block.attributes.forEach(attr => {
            if (attr.name === "backgroundColor") {
              bgColour = attr.value
            }
            if (attr.name === "textColor") {
              textColour = attr.value
            }
          })
        }
        classes.push("bg-" + bgColour)
        classes.push("text-" + textColour)
      }

      /* if (this.icon !== "") {
        classes.push(this.$style.hasIcon)
      }
      classes.push(this.$style[`${this.iconPos}Btn`]) */

      if (this.sharpCornersLeft) {
        classes.push(this.$style.scLeft)
      }
      if (this.sharpCornersRight) {
        classes.push(this.$style.scRight)
      }

      if (this.inactive) {
        classes.push(this.$style.inactive)
      }

      return classes
    },

    iconStyles() {
      const styles = []
      if (this.iconSize) {
        styles.push(`width: ${this.iconSize ? this.iconSize : 25}px`)
      }
      if (this.iconColor) {
        styles.push(`color: ${this.iconColor}`)
      }
      return styles
    },

    iconFontSize() {
      let size = 16
      if (this.iconSize) {
        size = this.iconSize
      } else {
        switch (this.size) {
          case "s":
            size = 16
            break
          case "m":
            size = 18
            break
          case "l":
            size = 21
            break
        }
      }
      return size
    }
  },

  methods: {
    onClick(event) {
      if (event && this.stopClick) event.stopPropagation()
      if (this.inactive) {
        return
      }

      if (this.to === "") {
        if (this.loading === null) {
          this.selfLoading = true
          setTimeout(() => {
            this.selfLoading = false
          }, 5000)
          document.addEventListener("visibilitychange", () => {
            this.selfLoading = false
          })
        }
      } else if (this.target) {
        window.open(this.to, this.target)
      } else {
        this.$router.push(this.to)
      }
    }
  }
}
</script>

<style lang="scss" module>
.btn {
  text-decoration: none;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.2s;
  display: inline-flex;
  align-items: center;
  padding: 1px;
  overflow: hidden;
  outline: none;
}

.btn + .btn {
  @media (min-width: var(--breakpoint-lg)) {
    margin-left: calc(var(--grid-gap) / 1.5);
  }
}

.slot {
  flex-grow: 1;
}

.block {
  display: flex;
  width: 100%;
}

.scLeft {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.scRight {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.inactive {
  border-color: white;
  cursor: default;
}

.iconAlign {
  @apply flex items-center justify-center;
}

// Colours
//FIXME: don't use scss variables
$blue: #41a5dd;
$red: #f74f64;
$green: #70c216;
$yellow: #f0ad4e;

.primary {
  composes: btn;
  @apply bg-blue-primary border border-solid border-blue-primary;
  color: white;
  &:hover:not(:disabled),
  &:active,
  &:focus {
    color: darken(white, 3%);
    background-color: darken($blue, 5%);
    border-color: darken($blue, 10%);
  }
  &:active {
    box-shadow: 0 0 0 3px lighten($blue, 10%);
  }
  &:disabled {
    opacity: 0.5;
    box-shadow: none;
    cursor: not-allowed;
  }

  a,
  a:hover {
    color: white;
    text-decoration: none;
  }
}

.yellow {
  composes: btn;
  background-color: $yellow;
  border: none;
  color: white;
  &:hover:not(:disabled) {
    color: darken(white, 3%);
    background-color: darken($yellow, 15%);
    border-color: darken($yellow, 20%);
  }

  a,
  a:hover {
    color: white;
    text-decoration: none;
  }
}

.red {
  composes: btn;
  @apply bg-pink-500 border border-transparent text-white;

  &:hover:not(:disabled),
  &:active {
    @apply bg-pink-600;
  }

  &:active {
    @apply border-pink-700;
  }

  &:disabled {
    @apply opacity-50 cursor-not-allowed;
  }

  a,
  a:hover {
    @apply text-white no-underline;
  }
}

.light {
  composes: btn;
  background-color: inherit;
  border: 1px solid darken(#f7f7f7, 15%);
  color: #6e6e6e;
  &:hover:not(:disabled),
  &:active {
    color: lighten(#6e6e6e, 3%);
    border-color: darken(#f7f7f7, 30%);
  }
  &:active {
    box-shadow: 0 0 0 3px darken(#f7f7f7, 10%);
  }
  &:disabled {
    color: #ccc;
    border-color: lighten(#f7f7f7, 30%);
    box-shadow: none;
    cursor: not-allowed;

    a {
      color: #ccc;
    }
  }

  a,
  a:hover {
    color: #6e6e6e;
    text-decoration: none;
  }
}

.superlight {
  composes: btn;
  background-color: inherit;
  border: 1px solid darken(#f7f7f7, 15%);
  color: #a4a4a4;
  &:hover:not(:disabled),
  &:active {
    color: lighten(#a4a4a4, 3%);
    border-color: darken(#f7f7f7, 15%);
  }
  &:active {
    box-shadow: 0 0 0 3px darken(#f7f7f7, 10%);
  }
  &:disabled {
    color: #ccc;
    border-color: darken(#f7f7f7, 5%);
    box-shadow: none;
    cursor: not-allowed;

    a {
      color: #ccc;
    }
  }

  a,
  a:hover {
    color: #a4a4a4;
    text-decoration: none;
  }
}

.green {
  composes: btn;
  @apply bg-green-primary border border-solid border-green-primary;
  color: white;
  &:hover:not(:disabled),
  &:active {
    color: darken(white, 3%);
    background-color: darken($green, 5%);
    border-color: darken($green, 10%);
  }
  &:active {
    box-shadow: 0 0 0 3px lighten($green, 10%);
  }
  &:disabled {
    opacity: 0.5;
    box-shadow: none;
    cursor: not-allowed;

    a {
      color: #eee;
    }
  }

  a,
  a:hover {
    color: white;
    text-decoration: none;
  }
}

.gray {
  @extend .light;
  @apply text-gray-700 bg-gray-310 border-gray-500;

  &:disabled {
    @apply text-gray-500 border-gray-400;

    a {
      @apply text-gray-500;
    }
  }

  &:hover:not(:disabled),
  &:active {
    @apply text-gray-700 bg-gray-400;
  }
}

.gray-100 {
  @extend .gray;
  @apply text-contrast-higher bg-gray-100 border-black20;

  &:disabled {
    background: #d9d9d9;
    @apply border-black20 text-white;
  }

  &:hover:not(:disabled),
  &:active {
    @apply text-contrast-higher bg-gray-100 border-black20;
  }
}

.gray-140 {
  @apply text-black70 bg-gray-140 border border-black20 rounded-md;

  &:disabled {
    @apply opacity-50;
  }
}

.lightgray {
  @extend .gray;
  @apply text-gray-700 bg-gray-220 border-gray-220;

  &:disabled {
    @apply border-gray-220;
  }

  &:hover:not(:disabled),
  &:active {
    @apply text-gray-700 bg-gray-300 border-gray-300;
  }
}

.darkgray {
  @extend .gray;
  @apply border-gray-220;

  background: #909090;
  color: white;

  &:disabled {
    @apply border-gray-220;
  }

  &:hover,
  &:active {
    @apply border-gray-300;

    filter: none;
    background: #979797;
    color: white;
  }
}

.cta-secondary {
  @extend .light;
  @apply text-white bg-anyvan-cta-secondary border-anyvan-cta-secondary;

  &:disabled {
    @apply bg-anyvan-cta-secondary border-anyvan-cta-secondary;
  }

  &:hover:not(:disabled),
  &:active {
    @apply text-white border-anyvan-cta-secondary;
    background: hsl(217, 62%, 25%);
  }
}

.rebrand-cta {
  composes: btn;
  @apply rounded-lg text-center;

  &:disabled {
    opacity: 50%;
  }

  &:hover:not(:disabled),
  &:active,
  &:focus {
    filter: saturate(1.5) brightness(0.9);
  }
}

.white {
  composes: btn;
  @apply bg-white text-black70 border border-black20 hover:bg-black10;
  &:hover:not(:disabled),
  &:active,
  &:focus {
    @apply bg-black10;
  }
  &:active,
  &:focus {
    box-shadow: 0 0 0 3px lighten(black, 10%);
  }
  &:disabled {
    opacity: 0.5;
    box-shadow: none;
    cursor: not-allowed;
  }

  a,
  a:hover {
    @apply text-black;
    text-decoration: none;
  }
}
</style>
